/*
Copyright Amazon.com, Inc. or its affiliates. All Rights Reserved.

Licensed under the Apache License, Version 2.0 (the "License").
You may not use this file except in compliance with the License.
You may obtain a copy of the License at

    http://www.apache.org/licenses/LICENSE-2.0

Unless required by applicable law or agreed to in writing, software
distributed under the License is distributed on an "AS IS" BASIS,
WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
See the License for the specific language governing permissions and
limitations under the License.
*/
import { SelectedFiltersDto } from '@ink-ai/portal/reducers/connector-filter';
import { Box, Chip, Popover, Typography } from '@mui/material';
import React from 'react';
import { connectorFilterActions } from '@ink-ai/portal/reducers/connector-filter';
import { useDispatch } from 'react-redux';
import { ConnectorFilterDtoTypeEnum } from '@ink-ai/insight-service-sdk';
import dayjs from 'dayjs';
interface SelectedFilterChipProps {
  item: SelectedFiltersDto;
  connectorIndex: number;
  filterIndex: number;
}

const SelectedFilterChip: React.FC<SelectedFilterChipProps> = (
  props: SelectedFilterChipProps,
) => {
  const { item, connectorIndex, filterIndex } = props;
  const [anchorEl, setAnchorEl] = React.useState<HTMLElement | null>(null);
  const handlePopoverOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handlePopoverClose = () => {
    setAnchorEl(null);
  };
  const dispatch = useDispatch();
  const open = Boolean(anchorEl);
  return (
    <>
      <Chip
        aria-owns={open ? 'mouse-over-popover' : undefined}
        aria-haspopup="true"
        onMouseEnter={handlePopoverOpen}
        onMouseLeave={handlePopoverClose}
        label={item.field.display_name}
        variant="outlined"
        onDelete={(e) => {
          console.info('e:', e);
          dispatch(
            connectorFilterActions.removeConnectorFilter({
              connectorIndex,
              filterIndex,
            }),
          );
        }}
        sx={{
          cursor: 'pointer',
          backgroundColor: '#fff',
          color: '#0288d1',
          borderColor: '#0288d1',
          '& .MuiChip-avatar': {
            color: '#0288d1',
          },
          '& .MuiChip-deleteIcon': {
            color: '#0288d1',
            visibility: 'visible',
          },
        }}
      />
      <Popover
        id="mouse-over-popover"
        sx={{
          pointerEvents: 'none',
        }}
        open={open}
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        onClose={handlePopoverClose}
        disableRestoreFocus
      >
        <Box sx={{ p: 1 }}>
          <Typography sx={{ color: '#888', fontSize: '14px' }}>
            {item?.operator?.label}
          </Typography>
          {(item.field.type === ConnectorFilterDtoTypeEnum.Number ||
            item.field.type === ConnectorFilterDtoTypeEnum.String) && (
            <Typography>
              {item?.selectedValue?.label ?? item.stringValue}
            </Typography>
          )}
          {item.field.type === ConnectorFilterDtoTypeEnum.Datetime && (
            <Typography>
              {dayjs(item?.dateValue).format('YYYY-MM-DD')}
            </Typography>
          )}
          {item.field.type === ConnectorFilterDtoTypeEnum.DateRange && (
            <Typography>
              {dayjs(item?.dateRangeStartTime).format('YYYY-MM-DD')}
              {' - '}
              {dayjs(item?.dateRangeEndTime).format('YYYY-MM-DD')}
            </Typography>
          )}
        </Box>
      </Popover>
    </>
  );
};

export default SelectedFilterChip;
